import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import EntityMerge from '../components/EntityMerge';
import GroupMembership from '../components/GroupMembership';
import CollapsibleSection from '../components/CollapsibleSection';
import UserManagement from '../components/UserManagement';
import WaveLoader from '../components/WaveLoader';

// Declarative schema for org configuration settings.
// To add a new setting: append one entry here — no other frontend changes needed.
const CONFIG_SCHEMA = [
  {
    key: 'default_time_range',
    label: 'Default time period',
    description: 'The time period selected by default when a user logs in or resets filters.',
    type: 'select',
    options: [
      { label: 'Last 7 days', value: '7' },
      { label: 'Last 30 days', value: '30' },
      { label: 'Last 90 days', value: '90' },
      { label: 'Last 12 months', value: '365' },
    ],
    default: '365',
  },
  {
    key: 'territory_place_matching',
    label: 'Field Team v2: place matching',
    description: 'Match the place named in each recording to the field staff member’s assigned sub-districts. Exact name matches are automatic; the rest become Review Queue cards. Matched sub-districts show as coverage on the Territory tab.',
    type: 'select',
    options: [
      { label: 'Off', value: 'off' },
      { label: 'On', value: 'on' },
    ],
    default: 'off',
  },
  {
    key: 'crop_issue_review',
    label: 'Field Team v2: crop–issue review',
    description: 'Crop and pest/disease mentions found close together in a conversation become Review Queue cards; links an admin confirms appear on the Territory tab.',
    type: 'select',
    options: [
      { label: 'Off', value: 'off' },
      { label: 'On', value: 'on' },
    ],
    default: 'off',
  },
];

const CONFIG_DEFAULTS = Object.fromEntries(CONFIG_SCHEMA.map(s => [s.key, s.default]));

function FieldHQ({ loading, setLoading, currentUser, isStaging, focusedConversationId, onFocusHandled, onOrgSettingsChange }) {
  const [quality, setQuality] = useState(null);
  const [activeSection, setActiveSection] = useState('conversations');
  const [pendingRecordings, setPendingRecordings] = useState({ data: [], error: null, source_matched: null, pending_count: null });
  const [processedRecordings, setProcessedRecordings] = useState({ data: [], error: null, processed_count: null });
  const [processedLoading, setProcessedLoading] = useState(false);
  const [expandedProcessed, setExpandedProcessed] = useState({});
  const [processedTranscriptCache, setProcessedTranscriptCache] = useState({});
  const [processedTranscriptLoading, setProcessedTranscriptLoading] = useState({});

  // Conversations sub-tab state
  const [conversations, setConversations] = useState([]);
  const [expandedTranscripts, setExpandedTranscripts] = useState({});
  const [transcriptCache, setTranscriptCache] = useState({});
  const [transcriptLoading, setTranscriptLoading] = useState({});
  const conversationRowRefs = useRef({});
  // Local copy of focusedConversationId that persists until scroll completes.
  // The prop is cleared immediately by onFocusHandled(); we need the ID to still
  // be available when the async conversation fetch finishes and the row renders.
  const [pendingFocusId, setPendingFocusId] = useState(null);


  // Organizations sub-tab state
  const [organizations, setOrganizations] = useState([]);

  // Configuration sub-tab state
  const [configSettings, setConfigSettings] = useState(CONFIG_DEFAULTS);
  const [configSaved, setConfigSaved] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState(null);

  // Opportunity entity types state
  const [oppTypes, setOppTypes] = useState([]);
  const [painTypes, setPainTypes] = useState([]);
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);
  const [oppTypeToAdd, setOppTypeToAdd] = useState('');
  const [oppTypesSaving, setOppTypesSaving] = useState(false);
  const [oppTypesSaved, setOppTypesSaved] = useState(false);
  const [oppTypesError, setOppTypesError] = useState(null);
  const [painTypesSaving, setPainTypesSaving] = useState(false);
  const [painTypesSaved, setPainTypesSaved] = useState(false);
  const [painTypesError, setPainTypesError] = useState(null);
  const [painTypeToAdd, setPainTypeToAdd] = useState('');
  const [sentimentTopicTypes, setSentimentTopicTypes] = useState([]);
  const [sentimentTopicTypeToAdd, setSentimentTopicTypeToAdd] = useState('');
  const [sentimentTopicTypesSaving, setSentimentTopicTypesSaving] = useState(false);
  const [sentimentTopicTypesSaved, setSentimentTopicTypesSaved] = useState(false);
  const [sentimentTopicTypesError, setSentimentTopicTypesError] = useState(null);

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin_dachido';
  const isDachidoAdmin = currentUser?.role === 'admin_dachido';

  const fetchPipelineData = () => {
    setLoading(true);
    axios.get('/api/field-hq/data-quality')
      .then(res => setQuality(res.data))
      .catch(err => console.error('Error loading HQ:', err))
      .finally(() => setLoading(false));
  };

  const fetchPendingRecordings = () => {
    axios.get('/api/field-hq/pending-recordings')
      .then(res => setPendingRecordings({
        data: res.data.data || [],
        error: res.data.error || null,
        source_matched: res.data.source_matched,
        pending_count: res.data.pending_count,
      }))
      .catch(err => setPendingRecordings({ data: [], error: err.message || 'Failed to load', source_matched: null, pending_count: null }));
  };

  const toggleProcessedTranscript = (row) => {
    const key = row.name || row.path;
    if (!key) return;
    const isOpen = expandedProcessed[key];
    if (isOpen) {
      setExpandedProcessed(prev => ({ ...prev, [key]: false }));
      return;
    }
    setExpandedProcessed(prev => ({ ...prev, [key]: true }));
    if (!processedTranscriptCache[key] && row.transcription_url) {
      setProcessedTranscriptLoading(prev => ({ ...prev, [key]: true }));
      // Fetch the transcription JSON directly from its SAS URL.
      // Use a plain axios call (no auth headers) so the SAS query string is the only credential.
      axios.get(row.transcription_url, { transformRequest: [(d, h) => { delete h.common; return d; }] })
        .then(res => setProcessedTranscriptCache(prev => ({ ...prev, [key]: res.data })))
        .catch(err => setProcessedTranscriptCache(prev => ({
          ...prev,
          [key]: { error: err.response?.data?.error || err.message || 'Failed to load transcript' }
        })))
        .finally(() => setProcessedTranscriptLoading(prev => ({ ...prev, [key]: false })));
    }
  };

  const fetchProcessedRecordings = () => {
    setProcessedLoading(true);
    axios.get('/api/field-hq/processed-recordings')
      .then(res => setProcessedRecordings({
        data: res.data.data || [],
        error: res.data.error || null,
        processed_count: res.data.processed_count,
      }))
      .catch(err => setProcessedRecordings({ data: [], error: err.message || 'Failed to load', processed_count: null }))
      .finally(() => setProcessedLoading(false));
  };

  const refreshPipelineSection = () => {
    fetchPipelineData();
    if (isAdmin) {
      fetchPendingRecordings();
      fetchProcessedRecordings();
    }
  };

  const fetchConversations = () => {
    setLoading(true);
    axios.get('/api/field-hq/conversations')
      .then(res => setConversations(res.data.data || []))
      .catch(err => console.error('Error loading conversations:', err))
      .finally(() => setLoading(false));
  };

  const toggleTranscript = (convId) => {
    const isOpen = expandedTranscripts[convId];
    if (isOpen) {
      setExpandedTranscripts(prev => ({ ...prev, [convId]: false }));
      return;
    }
    // Expand: fetch from blob if not cached
    setExpandedTranscripts(prev => ({ ...prev, [convId]: true }));
    if (!transcriptCache[convId]) {
      setTranscriptLoading(prev => ({ ...prev, [convId]: true }));
      axios.get(`/api/field-hq/conversation/${convId}/transcript`)
        .then(res => {
          setTranscriptCache(prev => ({ ...prev, [convId]: res.data }));
        })
        .catch(err => {
          const d = err.response?.data || {};
          const msg = d.error || err.message;
          const detail = d.tried_paths ? `\nTried: ${d.tried_paths.join(', ')}` : '';
          setTranscriptCache(prev => ({ ...prev, [convId]: { error: msg + detail } }));
        })
        .finally(() => setTranscriptLoading(prev => ({ ...prev, [convId]: false })));
    }
  };


  const fetchOrganizations = () => {
    setLoading(true);
    axios.get('/api/field-hq/organizations')
      .then(res => setOrganizations(res.data.organizations || []))
      .catch(err => console.error('Error loading organizations:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPipelineData();
  }, [setLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSection === 'pipeline' && isAdmin) {
      fetchPendingRecordings();
      fetchProcessedRecordings();
    } else if (activeSection === 'conversations' && isAdmin && conversations.length === 0) {
      fetchConversations();
    } else if (activeSection === 'organizations' && isDachidoAdmin && organizations.length === 0) {
      fetchOrganizations();
    } else if (activeSection === 'configuration' && isAdmin) {
      axios.get('/api/config/org-settings')
        .then(res => { if (res.data?.settings) setConfigSettings(s => ({ ...s, ...res.data.settings })); })
        .catch(() => {});
      axios.get('/api/config/opportunity-entity-types')
        .then(res => { if (res.data?.opportunity_entity_types) setOppTypes(res.data.opportunity_entity_types); })
        .catch(() => {});
      axios.get('/api/config/pain-entity-types')
        .then(res => { if (res.data?.pain_entity_types) setPainTypes(res.data.pain_entity_types); })
        .catch(() => {});
      axios.get('/api/config/sentiment-topic-entity-types')
        .then(res => { if (res.data?.sentiment_topic_entity_types) setSentimentTopicTypes(res.data.sentiment_topic_entity_types); })
        .catch(() => {});
      axios.get('/api/config/available-entity-types')
        .then(res => { if (res.data?.entity_types) setAvailableEntityTypes(res.data.entity_types); })
        .catch(() => {});
    }
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a focusedConversationId is set from Intel page drilldown:
  // capture it locally, switch tab, fetch conversations if needed, expand transcript.
  // onFocusHandled() is called immediately to clear the prop in the parent — the local
  // pendingFocusId carries the value forward until the row is scrolled into view.
  useEffect(() => {
    if (!focusedConversationId) return;
    setPendingFocusId(focusedConversationId);
    setActiveSection('conversations');
    if (conversations.length === 0) {
      setLoading(true);
      axios.get('/api/field-hq/conversations')
        .then(res => {
          setConversations(res.data.data || []);
        })
        .catch(err => console.error('Error loading conversations:', err))
        .finally(() => setLoading(false));
    }
    // Expand the transcript for focused conversation
    setExpandedTranscripts(prev => ({ ...prev, [focusedConversationId]: true }));
    if (!transcriptCache[focusedConversationId]) {
      setTranscriptLoading(prev => ({ ...prev, [focusedConversationId]: true }));
      axios.get(`/api/field-hq/conversation/${focusedConversationId}/transcript`)
        .then(res => setTranscriptCache(prev => ({ ...prev, [focusedConversationId]: res.data })))
        .catch(err => {
          const d = err.response?.data || {};
          const msg = d.error || err.message;
          const detail = d.tried_paths ? `\nTried: ${d.tried_paths.join(', ')}` : '';
          setTranscriptCache(prev => ({ ...prev, [focusedConversationId]: { error: msg + detail } }));
        })
        .finally(() => setTranscriptLoading(prev => ({ ...prev, [focusedConversationId]: false })));
    }
    if (onFocusHandled) onFocusHandled();
  }, [focusedConversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to and focus the target row once it is in the DOM.
  // Depends on pendingFocusId (not the prop) so the ID is still available
  // after the async conversation fetch completes and onFocusHandled() has run.
  // quality is included because the component returns a spinner while quality
  // is null — rows are not rendered (and refs not set) until quality arrives.
  // transcriptLoading is included because the transcript row renders below the
  // target row and shifts the layout; we wait for it to finish before scrolling.
  useEffect(() => {
    if (!pendingFocusId || conversations.length === 0 || !quality) return;
    if (transcriptLoading[pendingFocusId]) return; // wait for transcript to render
    const el = conversationRowRefs.current[pendingFocusId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
      setPendingFocusId(null);
    }
  }, [conversations, pendingFocusId, quality, transcriptLoading]);

  if (!quality) return <div className="loading"><WaveLoader size="large" /></div>;

  const sentimentPct = quality.sentiment_coverage_pct ?? 0;
  const totalConv = quality.total_conversations ?? 0;
  const entityMentions = quality.entity_mentions ?? 0;
  const issuesClassified = quality.issues_classified ?? 0;

  const tabStyle = (id) => ({
    padding: '10px 20px',
    border: 'none',
    borderBottom: activeSection === id ? '2px solid #1a1a1a' : '2px solid transparent',
    background: 'none',
    fontWeight: activeSection === id ? 600 : 400,
    color: activeSection === id ? '#1a1a1a' : '#6c757d',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '-1px',
  });

  const cardStyle = {
    background: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: '16px',
  };

  const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase' };
  const tdStyle = { padding: '14px 16px' };

  const sections = [
    ...(isAdmin ? [{ id: 'conversations', label: 'Conversations' }] : []),
    { id: 'pipeline', label: 'Pipeline' },
    ...(isAdmin ? [{ id: 'users', label: 'User Management' }] : []),
    ...(isDachidoAdmin ? [{ id: 'organizations', label: 'Organizations' }] : []),
    ...(isAdmin ? [{ id: 'configuration', label: 'Configuration' }] : []),
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div className="module-header" style={{ marginBottom: '24px' }}>
        <h2>Admin HQ</h2>
        <p>Pipeline execution, conversations & user management</p>
      </div>

      {/* Section tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '0',
        flexWrap: 'wrap',
      }}>
        {sections.map(s => (
          <button key={s.id} type="button" onClick={() => setActiveSection(s.id)} style={tabStyle(s.id)}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ============== Pipeline ============== */}
      {activeSection === 'pipeline' && (
        <div>
          <div style={cardStyle}>
            <div style={{
              padding: '16px 24px',
              background: '#1e3a5f',
              color: '#fff',
              fontWeight: 600,
              fontSize: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <span>NLP Pipeline Execution (Actual)</span>
              <button
                type="button"
                onClick={refreshPipelineSection}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1e3a5f',
                  background: '#fff',
                  border: '2px solid #fff',
                  borderRadius: '8px',
                  cursor: loading ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span aria-hidden>↻</span>
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={thStyle}>Processing Step</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Coverage</th>
                  <th style={thStyle}>Records Processed</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f1f3f5' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>Transcript Ingestion</td>
                  <td style={{ ...tdStyle, color: '#28a745' }}>Complete</td>
                  <td style={tdStyle}>100%</td>
                  <td style={tdStyle}>{totalConv}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f1f3f5' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>Sentiment Analysis</td>
                  <td style={{ ...tdStyle, color: '#28a745' }}>Complete</td>
                  <td style={tdStyle}>{totalConv ? sentimentPct.toFixed(1) : '0'}%</td>
                  <td style={tdStyle}>{totalConv}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f1f3f5' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>Entity Extraction</td>
                  <td style={{ ...tdStyle, color: '#28a745' }}>Complete</td>
                  <td style={tdStyle}>100%</td>
                  <td style={tdStyle}>{entityMentions}</td>
                </tr>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>Issue Classification</td>
                  <td style={{ ...tdStyle, color: '#28a745' }}>Complete</td>
                  <td style={tdStyle}>100%</td>
                  <td style={tdStyle}>{issuesClassified}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ padding: '12px 24px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', fontSize: '13px', color: '#495057' }}>
            <strong>Why still {totalConv}?</strong> Records Processed = rows in the database (<code>fact_conversation</code>). Files in Azure (Pending Recordings below) are <strong>not</strong> in this count until your ingestion pipeline writes them to the database. Run your pipeline to ingest the pending file — then click Refresh and the count will update.
          </div>
          </div>

          {/* Pending Recordings — subsection of Pipeline (admin only) */}
          {isAdmin && (
            <div style={cardStyle}>
              <div style={{
                padding: '14px 24px',
                background: '#e8f4fc',
                borderBottom: '2px solid #1e3a5f',
                fontWeight: 600,
                fontSize: '14px',
                color: '#1a1a1a',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <span>Pending Recordings</span>
                <button
                  type="button"
                  onClick={fetchPendingRecordings}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#fff',
                    background: '#1e3a5f',
                    border: '2px solid #0d2137',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                  }}
                >
                  <span aria-hidden>↻</span> Refresh pending list
                </button>
              </div>
              <div style={{ padding: '16px 24px' }}>
                {pendingRecordings.error && (
                  <p style={{ color: '#dc3545', marginBottom: '12px' }}>{pendingRecordings.error}</p>
                )}
                {!pendingRecordings.error && pendingRecordings.data.length === 0 && (
                  <>
                    <p style={{ color: '#6c757d', marginBottom: '16px' }}>
                      No pending recordings.
                    </p>
                    <button
                      type="button"
                      onClick={fetchPendingRecordings}
                      style={{
                        padding: '12px 24px',
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#fff',
                        background: '#28a745',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      <span aria-hidden>↻</span> Refresh pending list
                    </button>
                  </>
                )}
                {!pendingRecordings.error && pendingRecordings.data.length > 0 && (
                  <>
                    <p style={{ color: '#495057', marginBottom: '12px', fontSize: '13px' }}>
                      {pendingRecordings.source_matched} file(s) in source (transcriptions / dr.roof). {pendingRecordings.pending_count ?? 0} not yet in processed container.
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa' }}>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Recording date</th>
                            <th style={thStyle}>Last modified</th>
                            <th style={thStyle}>Size</th>
                            <th style={thStyle}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingRecordings.data.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '13px' }}>{(row.name || row.path || '').split('/').pop() || row.name || row.path}</td>
                              <td style={{ ...tdStyle, color: '#6c757d' }}>{row.recording_date || '–'}</td>
                              <td style={{ ...tdStyle, color: '#6c757d' }}>{row.last_modified ? new Date(row.last_modified).toLocaleString() : '–'}</td>
                              <td style={{ ...tdStyle, color: '#6c757d' }}>{row.size != null ? `${(row.size / 1024).toFixed(1)} KB` : '–'}</td>
                              <td style={tdStyle}>
                                {row.in_processed ? (
                                  <span style={{ color: '#6c757d' }}>In process</span>
                                ) : (
                                  <span style={{ color: '#28a745', fontWeight: 600 }}>Pending</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Processed Recordings — subsection of Pipeline (admin only) */}
          {isAdmin && (
            <div style={cardStyle}>
              <div style={{
                padding: '14px 24px',
                background: '#e8f4fc',
                borderBottom: '2px solid #1e3a5f',
                fontWeight: 600,
                fontSize: '14px',
                color: '#1a1a1a',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <span>Processed Recordings</span>
                <button
                  type="button"
                  onClick={fetchProcessedRecordings}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#fff',
                    background: '#1e3a5f',
                    border: '2px solid #0d2137',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                  }}
                >
                  <span aria-hidden>↻</span> Refresh processed list
                </button>
              </div>
              <div style={{ padding: '16px 24px' }}>
                {processedLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#6c757d', padding: '12px 0' }}>
                    <WaveLoader size="small" />
                    <span>Loading processed recordings…</span>
                  </div>
                )}
                {!processedLoading && processedRecordings.error && (
                  <p style={{ color: '#dc3545', marginBottom: '12px' }}>{processedRecordings.error}</p>
                )}
                {!processedLoading && !processedRecordings.error && processedRecordings.data.length === 0 && (
                  <p style={{ color: '#6c757d', marginBottom: '0' }}>
                    No processed recordings.
                  </p>
                )}
                {!processedLoading && !processedRecordings.error && processedRecordings.data.length > 0 && (
                  <>
                    <p style={{ color: '#495057', marginBottom: '12px', fontSize: '13px' }}>
                      {processedRecordings.processed_count ?? 0} processed recording(s).
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa' }}>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>User</th>
                            <th style={thStyle}>Recording</th>
                            <th style={thStyle}>Transcription</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processedRecordings.data.map((row, i) => {
                            const fileName = (row.name || row.path || '').split('/').pop() || row.name || row.path;
                            const userLabel = row.username || (row.user_id ? `user ${row.user_id}` : '–');
                            const key = row.name || row.path;
                            const isOpen = !!expandedProcessed[key];
                            const cached = processedTranscriptCache[key];
                            const isLoadingTx = !!processedTranscriptLoading[key];
                            const transcriptText = cached && !cached.error
                              ? (cached.translation || cached.transcription || (typeof cached === 'string' ? cached : JSON.stringify(cached, null, 2)))
                              : null;
                            return (
                              <React.Fragment key={i}>
                                <tr style={{ borderBottom: isOpen ? 'none' : '1px solid #f1f3f5' }}>
                                  <td style={{ ...tdStyle, color: '#6c757d' }}>{row.recording_date || '–'}</td>
                                  <td style={tdStyle}>{userLabel}</td>
                                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '13px' }}>
                                    {row.audio_url ? (
                                      <a href={row.audio_url} target="_blank" rel="noopener noreferrer">{fileName}</a>
                                    ) : (
                                      fileName
                                    )}
                                  </td>
                                  <td style={tdStyle}>
                                    {row.transcription_url ? (
                                      <button
                                        type="button"
                                        onClick={() => toggleProcessedTranscript(row)}
                                        style={{
                                          background: 'none', border: '1px solid #dee2e6', borderRadius: '4px',
                                          padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
                                          color: isOpen ? '#1e3a5f' : '#495057',
                                          fontWeight: isOpen ? 600 : 400,
                                        }}
                                      >
                                        {isOpen ? '▼ Hide' : '▶ Show'} ({row.transcription_status === 'processed' ? 'processed' : 'pending'})
                                      </button>
                                    ) : (
                                      <span style={{ color: '#6c757d' }}>None</span>
                                    )}
                                  </td>
                                </tr>
                                {isOpen && (
                                  <tr style={{ borderBottom: '1px solid #f1f3f5', background: '#f8f9fa' }}>
                                    <td colSpan={4} style={{ padding: '12px 24px' }}>
                                      {isLoadingTx && (<WaveLoader size="small" />)}
                                      {!isLoadingTx && cached?.error && (
                                        <div style={{ color: '#dc3545', fontSize: '13px' }}>{cached.error}</div>
                                      )}
                                      {!isLoadingTx && cached && !cached.error && (
                                        <div>
                                          {(cached.detected_language || cached.audio_duration || cached.word_count) && (
                                            <div style={{ marginBottom: '8px', fontSize: '12px', color: '#6c757d' }}>
                                              {cached.detected_language && <span style={{ marginRight: '16px' }}>Language: <strong>{cached.detected_language}</strong></span>}
                                              {cached.audio_duration && <span style={{ marginRight: '16px' }}>Duration: <strong>{cached.audio_duration}s</strong></span>}
                                              {cached.word_count && <span>Words: <strong>{cached.word_count}</strong></span>}
                                            </div>
                                          )}
                                          <pre style={{
                                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                            fontSize: '13px', lineHeight: '1.6',
                                            maxHeight: '400px', overflowY: 'auto',
                                            background: '#fff', border: '1px solid #dee2e6',
                                            borderRadius: '6px', padding: '12px',
                                            margin: 0,
                                          }}>
                                            {transcriptText || '(No transcript text)'}
                                          </pre>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============== Conversations ============== */}
      {activeSection === 'conversations' && isAdmin && (
        <div style={cardStyle}>
          <div style={{
            padding: '16px 24px',
            background: '#1e3a5f',
            color: '#fff',
            fontWeight: 600,
            fontSize: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>Conversations ({conversations.length})</span>
            <button type="button" onClick={fetchConversations} style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: '#1e3a5f',
              background: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer',
            }}>
              <span aria-hidden>↻</span> Refresh
            </button>
          </div>
          {conversations.length === 0 && !loading && (
            <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No conversations found.</div>
          )}
          {conversations.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>Start Time</th>
                    <th style={thStyle}>Location</th>
                    <th style={thStyle}>Agent</th>
                    <th style={thStyle}>Transcription</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((row) => {
                    const isFocused = row.conversation_id === pendingFocusId;
                    const isOpen = !!expandedTranscripts[row.conversation_id];
                    const cached = transcriptCache[row.conversation_id];
                    const isLoadingTx = !!transcriptLoading[row.conversation_id];
                    return (
                      <React.Fragment key={row.conversation_id}>
                        <tr
                          ref={el => { conversationRowRefs.current[row.conversation_id] = el; }}
                          tabIndex={isFocused ? -1 : undefined}
                          style={{
                            borderBottom: isOpen ? 'none' : '1px solid #f1f3f5',
                            background: isFocused ? '#fffbe6' : undefined,
                            outline: 'none',
                          }}
                        >
                          <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '13px', fontWeight: 600 }}>
                            {row.conversation_id}
                          </td>
                          <td style={{ ...tdStyle, color: '#6c757d' }}>
                            {row.call_date ? new Date(row.call_date).toLocaleDateString() : '–'}
                          </td>
                          <td style={tdStyle}>{row.region_name || '–'}</td>
                          <td style={{ ...tdStyle, fontWeight: 500 }}>{row.agent_name || '–'}</td>
                          <td style={tdStyle}>
                            <button
                              type="button"
                              onClick={() => toggleTranscript(row.conversation_id)}
                              style={{
                                background: 'none', border: '1px solid #dee2e6', borderRadius: '4px',
                                padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
                                color: isOpen ? '#1e3a5f' : '#495057',
                                fontWeight: isOpen ? 600 : 400,
                              }}
                            >
                              {isOpen ? '▼ Hide' : '▶ Show'}
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr style={{ borderBottom: '1px solid #f1f3f5', background: '#f8f9fa' }}>
                            <td colSpan={5} style={{ padding: '12px 24px' }}>
                              {isLoadingTx && (
                                <WaveLoader size="small" />
                              )}
                              {!isLoadingTx && cached?.error && (
                                <div style={{ color: '#dc3545', fontSize: '13px' }}>{cached.error}</div>
                              )}
                              {!isLoadingTx && cached && !cached.error && (
                                <div>
                                  {cached.metadata && (cached.metadata.detected_language || cached.metadata.audio_duration) && (
                                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#6c757d' }}>
                                      {cached.metadata.detected_language && <span style={{ marginRight: '16px' }}>Language: <strong>{cached.metadata.detected_language}</strong></span>}
                                      {cached.metadata.audio_duration && <span style={{ marginRight: '16px' }}>Duration: <strong>{cached.metadata.audio_duration}s</strong></span>}
                                      {cached.metadata.word_count && <span>Words: <strong>{cached.metadata.word_count}</strong></span>}
                                    </div>
                                  )}
                                  <pre style={{
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                    fontSize: '13px', lineHeight: '1.6',
                                    maxHeight: '400px', overflowY: 'auto',
                                    background: '#fff', border: '1px solid #dee2e6',
                                    borderRadius: '6px', padding: '12px',
                                    margin: 0,
                                  }}>
                                    {cached.transcript || '(No transcript text)'}
                                  </pre>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============== User Management ============== */}
      {activeSection === 'users' && isAdmin && (
        <UserManagement currentUser={currentUser} isStaging={isStaging} />
      )}

      {/* ============== Organizations (Dachido Admin only) ============== */}
      {activeSection === 'organizations' && isDachidoAdmin && (
        <div style={cardStyle}>
          <div style={{
            padding: '16px 24px',
            background: '#1e3a5f',
            color: '#fff',
            fontWeight: 600,
            fontSize: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>Registered Organizations</span>
            <button type="button" onClick={fetchOrganizations} style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: '#1e3a5f',
              background: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer',
            }}>
              <span aria-hidden>↻</span> Refresh
            </button>
          </div>
          {organizations.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Display Name</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Company Code</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '13px' }}>{org.name}</td>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{org.display_name}</td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: org.type === 'agri' ? '#d4edda' : '#e8f4fc',
                          color: org.type === 'agri' ? '#155724' : '#1e3a5f',
                        }}>
                          {org.type === 'agri' ? 'Agriculture' : 'Sales'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: '#6c757d' }}>{org.company_code || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No organizations found</div>
          )}
        </div>
      )}

      {/* ============== Configuration (Admin only) ============== */}
      {activeSection === 'configuration' && isAdmin && (
        <div style={cardStyle}>
          <div style={{ padding: '16px 24px', background: '#1e3a5f', color: '#fff', fontWeight: 600, fontSize: '15px' }}>
            Configuration
          </div>
          <div style={{ padding: '28px 24px' }}>
            {CONFIG_SCHEMA.map(field => (
              <div key={field.key} style={{ marginBottom: '28px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
                  {field.label}
                </div>
                {field.description && (
                  <p style={{ fontSize: '13px', color: '#6c757d', margin: '0 0 10px' }}>{field.description}</p>
                )}
                {field.type === 'select' && (
                  <select
                    value={configSettings[field.key] ?? field.default}
                    onChange={e => { setConfigSettings(s => ({ ...s, [field.key]: e.target.value })); setConfigSaved(false); }}
                    style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '200px', background: '#fff' }}
                  >
                    {field.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <button
                type="button"
                disabled={configSaving}
                onClick={() => {
                  setConfigSaving(true);
                  setConfigSaved(false);
                  setConfigError(null);
                  axios.post('/api/config/org-settings', { settings: configSettings })
                    .then(res => {
                      setConfigSaved(true);
                      if (onOrgSettingsChange) onOrgSettingsChange(res.data.settings || configSettings);
                    })
                    .catch(err => setConfigError(err.response?.data?.error || 'Failed to save'))
                    .finally(() => setConfigSaving(false));
                }}
                style={{
                  padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#fff',
                  background: '#1e3a5f', border: 'none', borderRadius: '8px',
                  cursor: configSaving ? 'wait' : 'pointer', opacity: configSaving ? 0.7 : 1,
                }}
              >
                {configSaving ? 'Saving…' : 'Save'}
              </button>
              {configSaved && <span style={{ fontSize: '13px', color: '#28a745', fontWeight: 600 }}>Saved</span>}
              {configError && <span style={{ fontSize: '13px', color: '#dc3545' }}>{configError}</span>}
            </div>

            {/* Opportunity Entity Types */}
            <div style={{ marginTop: '36px', borderTop: '1px solid #e9ecef', paddingTop: '28px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
                Opportunity Entity Types
              </div>
              <p style={{ fontSize: '13px', color: '#6c757d', margin: '0 0 14px' }}>
                Entity types used for opportunity scoring in Sales Strategy. Only these types will be included in the opportunity matrix and scoring.
              </p>

              {/* Current list */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {oppTypes.length === 0 && (
                  <span style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic' }}>
                    No types configured — all entity types will be used.
                  </span>
                )}
                {oppTypes.map(t => (
                  <span key={t} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '4px 10px', borderRadius: '16px', fontSize: '13px', fontWeight: 500,
                    background: '#e8f4fc', color: '#1e3a5f', border: '1px solid #b8d9f0',
                  }}>
                    {t}
                    <button type="button" onClick={() => { setOppTypes(prev => prev.filter(x => x !== t)); setOppTypesSaved(false); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 700, fontSize: '14px', padding: 0, lineHeight: 1 }}
                      title="Remove"
                    >&times;</button>
                  </span>
                ))}
              </div>

              {/* Add from available */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <select
                  value={oppTypeToAdd}
                  onChange={e => setOppTypeToAdd(e.target.value)}
                  style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '200px', background: '#fff' }}
                >
                  <option value="" disabled>Add entity type…</option>
                  {availableEntityTypes.filter(t => !oppTypes.includes(t)).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <button type="button" onClick={() => {
                  if (oppTypeToAdd && !oppTypes.includes(oppTypeToAdd)) {
                    setOppTypes(prev => [...prev, oppTypeToAdd]);
                    setOppTypesSaved(false);
                    setOppTypeToAdd('');
                  }
                }} style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: '#fff',
                  background: '#28a745', border: 'none', borderRadius: '6px', cursor: 'pointer',
                }}>
                  Add
                </button>
              </div>

              {/* Save */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button type="button" disabled={oppTypesSaving} onClick={() => {
                  setOppTypesSaving(true);
                  setOppTypesSaved(false);
                  setOppTypesError(null);
                  axios.post('/api/config/opportunity-entity-types', { opportunity_entity_types: oppTypes })
                    .then(() => setOppTypesSaved(true))
                    .catch(err => setOppTypesError(err.response?.data?.error || 'Failed to save'))
                    .finally(() => setOppTypesSaving(false));
                }} style={{
                  padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#fff',
                  background: '#1e3a5f', border: 'none', borderRadius: '8px',
                  cursor: oppTypesSaving ? 'wait' : 'pointer', opacity: oppTypesSaving ? 0.7 : 1,
                }}>
                  {oppTypesSaving ? 'Saving…' : 'Save'}
                </button>
                {oppTypesSaved && <span style={{ fontSize: '13px', color: '#28a745', fontWeight: 600 }}>Saved</span>}
                {oppTypesError && <span style={{ fontSize: '13px', color: '#dc3545' }}>{oppTypesError}</span>}
              </div>
            </div>

            {/* Pain Entity Types */}
            <div style={{ marginTop: '36px', borderTop: '1px solid #e9ecef', paddingTop: '28px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
                Pain Entity Types
              </div>
              <p style={{ fontSize: '13px', color: '#6c757d', margin: '0 0 14px' }}>
                Entity types used for pain signal detection in Field Signal and Field Intel. Only these types will be classified as customer friction signals.
              </p>

              {/* Current list */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {painTypes.length === 0 && (
                  <span style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic' }}>
                    No types configured — defaults will be used.
                  </span>
                )}
                {painTypes.map(t => (
                  <span key={t} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '4px 10px', borderRadius: '16px', fontSize: '13px', fontWeight: 500,
                    background: '#fce4e4', color: '#8b1a1a', border: '1px solid #f0b0b0',
                  }}>
                    {t}
                    <button type="button" onClick={() => { setPainTypes(prev => prev.filter(x => x !== t)); setPainTypesSaved(false); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 700, fontSize: '14px', padding: 0, lineHeight: 1 }}
                      title="Remove"
                    >&times;</button>
                  </span>
                ))}
              </div>

              {/* Add from available */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <select
                  value={painTypeToAdd}
                  onChange={e => setPainTypeToAdd(e.target.value)}
                  style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '200px', background: '#fff' }}
                >
                  <option value="" disabled>Add entity type…</option>
                  {availableEntityTypes.filter(t => !painTypes.includes(t)).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <button type="button" onClick={() => {
                  if (painTypeToAdd && !painTypes.includes(painTypeToAdd)) {
                    setPainTypes(prev => [...prev, painTypeToAdd]);
                    setPainTypesSaved(false);
                    setPainTypeToAdd('');
                  }
                }} style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: '#fff',
                  background: '#28a745', border: 'none', borderRadius: '6px', cursor: 'pointer',
                }}>
                  Add
                </button>
              </div>

              {/* Save */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button type="button" disabled={painTypesSaving} onClick={() => {
                  setPainTypesSaving(true);
                  setPainTypesSaved(false);
                  setPainTypesError(null);
                  axios.post('/api/config/pain-entity-types', { pain_entity_types: painTypes })
                    .then(() => setPainTypesSaved(true))
                    .catch(err => setPainTypesError(err.response?.data?.error || 'Failed to save'))
                    .finally(() => setPainTypesSaving(false));
                }} style={{
                  padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#fff',
                  background: '#1e3a5f', border: 'none', borderRadius: '8px',
                  cursor: painTypesSaving ? 'wait' : 'pointer', opacity: painTypesSaving ? 0.7 : 1,
                }}>
                  {painTypesSaving ? 'Saving…' : 'Save'}
                </button>
                {painTypesSaved && <span style={{ fontSize: '13px', color: '#28a745', fontWeight: 600 }}>Saved</span>}
                {painTypesError && <span style={{ fontSize: '13px', color: '#dc3545' }}>{painTypesError}</span>}
              </div>
            </div>

            {/* Sentiment Topic Entity Types */}
            <div style={{ marginTop: '36px', borderTop: '1px solid #e9ecef', paddingTop: '28px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
                Sentiment Topic Entity Types
              </div>
              <p style={{ fontSize: '13px', color: '#6c757d', margin: '0 0 14px' }}>
                Entity types shown as topics on the Brand Sentiment page (e.g. Company, Product). Only mention types with per-mention sentiment scoring belong here.
              </p>

              {/* Current list */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {sentimentTopicTypes.length === 0 && (
                  <span style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic' }}>
                    No types configured — defaults (Company, Product) will be used.
                  </span>
                )}
                {sentimentTopicTypes.map(t => (
                  <span key={t} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '4px 10px', borderRadius: '16px', fontSize: '13px', fontWeight: 500,
                    background: '#e8f4fc', color: '#1e3a5f', border: '1px solid #b8d9f0',
                  }}>
                    {t}
                    <button type="button" onClick={() => { setSentimentTopicTypes(prev => prev.filter(x => x !== t)); setSentimentTopicTypesSaved(false); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 700, fontSize: '14px', padding: 0, lineHeight: 1 }}
                      title="Remove"
                    >&times;</button>
                  </span>
                ))}
              </div>

              {/* Add from available */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <select
                  value={sentimentTopicTypeToAdd}
                  onChange={e => setSentimentTopicTypeToAdd(e.target.value)}
                  style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '200px', background: '#fff' }}
                >
                  <option value="" disabled>Add entity type…</option>
                  {availableEntityTypes.filter(t => !sentimentTopicTypes.includes(t)).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <button type="button" onClick={() => {
                  if (sentimentTopicTypeToAdd && !sentimentTopicTypes.includes(sentimentTopicTypeToAdd)) {
                    setSentimentTopicTypes(prev => [...prev, sentimentTopicTypeToAdd]);
                    setSentimentTopicTypesSaved(false);
                    setSentimentTopicTypeToAdd('');
                  }
                }} style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: '#fff',
                  background: '#28a745', border: 'none', borderRadius: '6px', cursor: 'pointer',
                }}>
                  Add
                </button>
              </div>

              {/* Save */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button type="button" disabled={sentimentTopicTypesSaving} onClick={() => {
                  setSentimentTopicTypesSaving(true);
                  setSentimentTopicTypesSaved(false);
                  setSentimentTopicTypesError(null);
                  axios.post('/api/config/sentiment-topic-entity-types', { sentiment_topic_entity_types: sentimentTopicTypes })
                    .then(() => setSentimentTopicTypesSaved(true))
                    .catch(err => setSentimentTopicTypesError(err.response?.data?.error || 'Failed to save'))
                    .finally(() => setSentimentTopicTypesSaving(false));
                }} style={{
                  padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#fff',
                  background: '#1e3a5f', border: 'none', borderRadius: '8px',
                  cursor: sentimentTopicTypesSaving ? 'wait' : 'pointer', opacity: sentimentTopicTypesSaving ? 0.7 : 1,
                }}>
                  {sentimentTopicTypesSaving ? 'Saving…' : 'Save'}
                </button>
                {sentimentTopicTypesSaved && <span style={{ fontSize: '13px', color: '#28a745', fontWeight: 600 }}>Saved</span>}
                {sentimentTopicTypesError && <span style={{ fontSize: '13px', color: '#dc3545' }}>{sentimentTopicTypesError}</span>}
              </div>
            </div>

            {/* The two entity-curation tools. Both are per-org configuration — merge says
                two names are one thing, group membership says which category that thing
                belongs to — and both are tall, so they open on demand rather than pushing
                the settings above them off the screen. Collapsed also means unmounted, so
                neither fetches until someone actually opens it. */}
            <div style={{ marginTop: '36px', paddingTop: '28px', borderTop: '1px solid #e9ecef' }}>
              <CollapsibleSection
                title="Merge duplicate entities"
                subtitle="Collapse spelling variants of the same thing into one entity.">
                <EntityMerge currentUser={currentUser} />
              </CollapsibleSection>
              <CollapsibleSection
                title="Group membership"
                subtitle="Which entities sit in which curated category.">
                <GroupMembership currentUser={currentUser} />
              </CollapsibleSection>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FieldHQ;
